import { Fragment, useRef, useState } from 'react';
import type { IUserSettingsPrpFormatRow } from 'app/services/userSettingsApi';
import { useRailSortable } from '../../useRailSortable';
import { deleteButtonWantsTop } from '../submittal/submittalModel';

/**
 * Column 1 of the Proposal tab — `proposal.tmpl:5-40`. The ordered table of
 * contents of the Proposal document.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★★ EVERY CLASS AND ID BELOW DIFFERS FROM THE SUBMITTAL RAIL. THAT IS THE
 * WHOLE REASON THIS IS A SECOND FILE AND NOT A `variant` PROP. ★★
 *
 *   submittal                                  proposal
 *   ─────────────────────────────────────────  ────────────────────────────────
 *   .submittal-content-submittal-format        .proposal-content-oproposal-format
 *   .left-column-user-settings-scrollable      .left-column-proposal-scrollable
 *   #submittal-format-list-wrapper[-row]       #proposal-format-list-wrapper[-row]
 *   .submittal-item-for-drag                   .item-for-drag
 *   <span class="submittal_format_field">      <span class="prօposal_format_field">
 *   .dropdown.dropdownprop-format              .dropdown.dropdownprop-format.col-1
 *   btn class "ka-has-tooltip btn  dropdown…"  "btn ka-has-tooltip dropdown…"
 *
 * ★★★ Q13 — `prօposal_format_field` CONTAINS A U+0585 ARMENIAN SMALL LETTER OH
 * (`օ`) WHERE A LATIN `o` BELONGS. It is spelled that way consistently in
 * `proposal.tmpl:24`, `proposal.format.tmpl:6`, `proposal.format.item.tmpl:4`
 * and `proposal.js:665`, so it works — and `user_settings/styles.css` is 69 KB
 * of selectors that may key off it. The byte is copied verbatim. NEVER
 * "normalise" it, and never let an editor's ASCII-fold do it for you: the
 * Partners lesson (legacy-CSS-selector-miss) is exactly this failure.
 *
 * ★ TWO TEMPLATES, ONE DIFFERENCE. `render()` emits the rows from
 * `proposal.tmpl:19-38` and terminates them with a `<div class="select-row
 * empty-row">` sentinel — the "append to the end" drop target.
 * `render_selects()` (`proposal.js:184-204`) replaces
 * `#proposal-format-list-wrapper`'s innerHTML with `proposal.format.tmpl`, which
 * re-emits the wrapper div and the rows but **NOT the sentinel**. (Unlike
 * submittal, whose two templates also disagree about class ORDER, proposal's two
 * are otherwise identical.) `render_selects` is reached from exactly one place —
 * deleting a User Defined Field (`:327`) — so after that one action the rail
 * legitimately has no sentinel until the next full render. `variant` selects.
 *
 * ★ `ui-sortable` / `ui-sortable-handle` are in the live DOM for both variants:
 * `proposal.tmpl:19` hard-codes the former and `makeSortable()` (`:606-656`)
 * stamps both after either render. Neither has a CSS reference; they are
 * rendered for DOM parity.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★★★ Q12 — WHY THERE IS NO "INJECTED ROW" VARIANT HERE. ★★★
 *
 * `proposal.js:21` really does compile `templateSelectItem` from
 * `submittal/tmpl/submittal.format.item.tmpl`, and `createNewField` really does
 * build a submittal-flavoured row out of it after a drop. That row never
 * reaches the live DOM: `field.set(...)` on the line above fires the format
 * collection's `'change'` → `render()` SYNCHRONOUSLY, `$el.html()` throws the
 * rail away, and the `$nextRow`/`$prevRow` anchors `createNewField` captured
 * before its two `await`s are detached by the time `$nextRow.before(...)` runs.
 * The `$wrapper.prepend(...)` branch would reach the live wrapper, but it needs
 * `ui.item` to have no sibling `.select-row[data-index]` at all — an empty rail.
 *
 * ⚠ MEASURED (wave-4 verify, third PATCH stalled 9 s): the row legacy shows for
 * that round-trip is the COLLECTION's, from `proposal.tmpl` — `row-<real id>`,
 * `item-for-drag`, `ui-sortable-handle`, `<span class="prօposal_format_field">`,
 * an EMPTY label (the POST's `name` is null), `data-model=""`, trash
 * `data-id="<real id>"` and `data-parent=".left-column-proposal-scrollable"` —
 * and it IS draggable: dragging it emitted a full `/prp/format/drag/` PATCH
 * carrying its real id. So every row this component renders comes from the
 * collection, and the drop window differs from the settled state only in the
 * label text and `data-model`/`data-target`, which the tail render fills in.
 *
 * ★ THE DRAG IS SPLIT THE SAME WAY AS THE SUBMITTAL RAIL, and both halves are
 * POINTER drags because both jQuery-UI widgets are: the INTERNAL reorder is
 * `useRailSortable` (so `axis: "y"`, the helper's containment, the
 * `ui-sortable-placeholder` hole and a list whose geometry does not move
 * mid-drag all survive the port) and the LIBRARY drag is `useLibraryDrag` (so
 * the dragged row is a visible clone, the drop can land nowhere but this rail,
 * and the gap it opens is continuous). Neither binds a `drag*` handler. See
 * `submittal/FormatRail.tsx`'s banner and the two hooks', which document the
 * reasoning at length. The proposal sortable's own options differ in exactly
 * one place that matters:
 * `items: ".item-for-drag"` (`:616`) instead of `.submittal-item-for-drag`, and
 * `init_draggable($(".draggable-item"))` (`:613`) instead of
 * `.submittal-draggable-item` — both of which live on the library anchors, so
 * they are `ProposalFieldLibrary`'s concern.
 *
 * ★ Q22 — a plain click on a row emits a full no-op `/prp/format/drag/` PATCH,
 * because jQuery-UI fires `stop` for a zero-distance drag. Reproduced, with
 * jQuery-UI's `distance: 1` threshold and its `cancel` list, exactly as the
 * submittal rail does.
 */

export type ProposalFormatRailVariant = 'render' | 'renderSelects';

interface ProposalFormatRailProps {
  rows: IUserSettingsPrpFormatRow[];
  /** `render_selects`' template drops the `.empty-row` sentinel. */
  variant: ProposalFormatRailVariant;
  /** `self.active_row_id` — the `row-<id>` of the `.active` row, or `null`. */
  activeRowId: string | null;
  /** Row ids whose label overflows — `showTooltip` (`:665-670`) puts
   * `.with_tooltip_element` on the span's parent, i.e. the `.select-row`. */
  tooltipRowIds: ReadonlySet<number>;
  /** `rowClick` (`:672-692`). `domId` is what legacy reads back out of the DOM
   * as `self.active_row_id` (`:677`) — always `String(row.id)` now that every
   * rendered row comes from the collection. */
  onRowClick: (row: IUserSettingsPrpFormatRow, domId: string) => void;
  /** `#proposal-format-list-wrapper`'s `onscroll` → `check_scroll` (`:552-559`). */
  onWrapperScroll: (wrapper: HTMLDivElement) => void;
  /** Handed up so `render()` can restore `scrollTop` and `showTooltip` can
   * measure the labels. */
  wrapperRef: React.MutableRefObject<HTMLDivElement | null>;
  labelRefs: React.MutableRefObject<Map<number, HTMLSpanElement>>;
  /** The sortable container itself — `#proposal-format-list-wrapper-row`, the
   * element `connectToSortable` names. Handed up because `useLibraryDrag` has
   * to measure it: it is the one region a dragged library anchor may be
   * released over. */
  railRef: React.MutableRefObject<HTMLDivElement | null>;
  /** Where the LIBRARY drag would insert, or `null` when its clone is not over
   * the rail — jQuery-UI's `ui-sortable-placeholder`, owned by `useLibraryDrag`
   * because that widget is the one that moves it. */
  libraryPlaceholderIndex: number | null;
  /** Live move during an internal drag — the sortable shuffling its items. */
  onRowsMove: (from: number, to: number) => void;
  /** The internal-drag half of `stop` (`:625-635`) → §6 W7. */
  onReorderCommit: () => void;
  /** ★ Q22 — the zero-distance `stop`. Same payload as `onReorderCommit`. */
  onRowPlainClick: () => void;
  /**
   * `click .delete` → `render_delete_popup_proposal` (`:458-470`).
   * ★ Q4 — called TWICE per click: the `<button class="… delete">` and the
   * nested `<i class="icon-trash delete">` both match, and
   * `render_delete_popup_proposal` never calls `stopPropagation` (unlike
   * `render_popup`, `:439`). Two identical popups, one DELETE.
   */
  onDeleteClick: (domId: string) => void;
  /** ★ Q18 — `#add_field_to_structure` → `addStructureField` (`:487-493`). The
   * button is hard-coded `hide` AND lives inside a `hidden` header row, so it is
   * unreachable — but unlike submittal, the PROPOSAL view really does bind it
   * (`:45`), so the handler is wired. */
  onAddStructureField: () => void;
}

/** jQuery-UI sortable's default `cancel` option. A press on any of these never
 * starts a sort — which is why clicking the trash emits no drag PATCH. */
const SORTABLE_CANCEL = 'input,textarea,button,select,option';

const ProposalFormatRail = ({
  rows,
  variant,
  activeRowId,
  tooltipRowIds,
  onRowClick,
  onWrapperScroll,
  wrapperRef,
  labelRefs,
  railRef,
  libraryPlaceholderIndex,
  onRowsMove,
  onReorderCommit,
  onRowPlainClick,
  onDeleteClick,
  onAddStructureField,
}: ProposalFormatRailProps) => {
  /** ★ Q20's `.top` flip — a Bootstrap dropdown direction for a `.dropdown-menu`
   * that does not exist anywhere on this page. */
  const [topRows, setTopRows] = useState<ReadonlySet<number>>(() => new Set());
  /** jQuery-UI's `ui-sortable-placeholder` for a LIBRARY drag — a PROP now,
   * because the library drag is a pointer drag (`useLibraryDrag`) and the widget
   * that owns the clone owns the hole it opens, exactly as one jQuery-UI
   * `mousemove` moves both. */
  const placeholderIndex = libraryPlaceholderIndex;
  /**
   * ★ U-d (2026-09-09) — the INTERNAL reorder: the lifted row, its Y-only
   * travel, its containment and the hole it leaves behind. Legacy's
   * `sortable({axis: 'y'})` (`proposal.js:615-617`).
   */
  const sortable = useRailSortable({
    rowCount: rows.length,
    scrollerRef: wrapperRef,
    cancelSelector: SORTABLE_CANCEL,
    onRowsMove,
    onCommit: onReorderCommit,
  });
  /** ★ Q22's other half — jQuery-UI's `distance: 1`. A press-and-release that
   * never moves a pixel starts no sort and therefore emits no PATCH. */
  const pressPointRef = useRef<{ x: number; y: number } | null>(null);
  /**
   * ★ Where the dragged row's own slot is reproduced.
   *
   * jQuery-UI lifts the item (`position: absolute`, `axis: 'y'`) and leaves an
   * invisible `ui-sortable-placeholder` of the SAME height in the list, so the
   * rail keeps exactly n slots of 40px for the whole drag: the rows part around
   * the hole, nothing re-flows, and the hook's cached geometry stays true from
   * `mousedown` to `mouseup`.
   *
   * ★ `stop` fires on EVERY mouseup that followed a started sort, even when the
   * row landed back where it started, so `onCommit` is wired straight to
   * `onReorderCommit` with deliberately NO "skip if unchanged" guard.
   */
  const internalDragIndex = sortable.drag?.index ?? null;

  const handleDeleteMouseOver = (rowId: number, target: HTMLElement) => {
    const wants = deleteButtonWantsTop(target);
    setTopRows((prev) => {
      if (wants === prev.has(rowId)) return prev;
      const next = new Set(prev);
      if (wants) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  };

  return (
    <div className="col-xs-3 full-height left-column proposal-content-oproposal-format">
      {/* `proposal.tmpl:6-16`. Permanently invisible — the header row is
          `hidden` and ★ Q18 hard-codes `hide` on the button too. Rendered, and
          WIRED: `proposal.js:45` binds `addStructureField` to it, which submittal
          does not. */}
      <div className="row block-header proposal-format-header hidden">
        <div className="col-xs-12 col-md-8 col-lg-8">
          <i className="icon-baseline-description-24px" />
          <span className="block-title">Proposal Format</span>
        </div>
        <div className="col-xs-12 col-md-4 col-lg-4 block-header-button-add">
          <button
            id="add_field_to_structure"
            className="btn-add btn add hide"
            onClick={onAddStructureField}
          >
            <i style={{ fontSize: '17px', paddingRight: '6px' }}>+</i> ADD
          </button>
        </div>
      </div>
      <div className="drag-note">Drag and drop to add or move a field.</div>
      <div
        className="left-column-proposal-scrollable"
        id="proposal-format-list-wrapper"
        ref={wrapperRef}
        onScroll={(e) => onWrapperScroll(e.currentTarget)}
      >
        {/* ★★ The drop zone is `#proposal-format-list-wrapper-row`, the element
            the sortable is bound to — NOT the scroll wrapper. With the sentinel
            present its box extends one row below the last real row, which is what
            makes "append to the end" reachable; after a UDF delete
            `render_selects` drops the sentinel and that gap closes until the next
            full render. Legacy has exactly that gap; measuring the wrapper would
            invent an append path legacy does not have — so `railRef` is bound
            HERE, and `useLibraryDrag` hit-tests exactly this box. */}
        <div
          className="row ui-sortable"
          id="proposal-format-list-wrapper-row"
          ref={(el) => {
            sortable.containerRef.current = el;
            railRef.current = el;
          }}
          /* The helper is absolutely positioned, so the sortable container has
             to be its offset parent — which is also what keeps it inside the
             list. Only while a drag is in flight, so no stacking context is
             created at rest. */
          style={sortable.drag ? { position: 'relative' } : undefined}
        >
          {rows.map((row, index) => {
            const target = row.object_id == null ? '' : String(row.object_id);
            const domId = String(row.id);
            const label = row.name ?? '';
            return (
              /* A keyed Fragment, NOT a wrapper element: `styles.css` and
                 legacy's own renumbering loop
                 (`#proposal-format-list-wrapper-row > div.select-row:not(.empty-row)`)
                 both use direct-child selectors, so the rows must stay direct
                 children. */
              <Fragment key={row.id}>
                {placeholderIndex === index && <ProposalRailPlaceholder />}
                {/* The lifted row's own hole — see `internalDragIndex`. */}
                {internalDragIndex === index && sortable.drag && (
                  <ProposalRailPlaceholder height={sortable.drag.height} />
                )}
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                <div
                  className={[
                    'col-xs-12',
                    'item-for-drag',
                    'select-row',
                    'select-row-click',
                    activeRowId === `row-${domId}` ? 'active' : '',
                    tooltipRowIds.has(row.id) ? 'with_tooltip_element' : '',
                    // jQuery-UI's `_setHandleClassName`, appended last.
                    'ui-sortable-handle',
                    // jQuery-UI stamps this on the item it lifts;
                    // `styles.css:2134` tints it `#f2efed` while it travels.
                    internalDragIndex === index ? 'ui-sortable-helper' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={sortable.helperStyle(index)}
                  id={`row-${domId}`}
                  data-index={index}
                  data-name={row.name ?? ''}
                  data-target={target}
                  data-model={row.model ?? ''}
                  data-cms={row.cms_field_name ?? ''}
                  /* ★ NO `draggable` attribute — legacy's rows have none
                     either, because jQuery-UI drives the sort off raw mouse
                     events, and so do BOTH pointer drags on this page:
                     `useRailSortable` here and `useLibraryDrag` in the middle
                     column. No `drag*` handler is bound anywhere on the rail
                     for the same reason. */
                  onMouseDown={(e) => {
                    pressPointRef.current = { x: e.clientX, y: e.clientY };
                    sortable.onRowMouseDown(e, index);
                  }}
                  onClick={(e) => {
                    // ★ Q22 — suppressed exactly where jQuery-UI would never have
                    // started (and therefore never stopped) a sort: a press on a
                    // `cancel`ed control, a press below the `distance: 1`
                    // threshold, the click trailing a real drag, and — U-f — a
                    // PROGRAMMATIC click.
                    //
                    // ★ U-f: `detail === 0` is `HTMLElement.click()`, i.e. the
                    // click the ↑/↓ walk synthesises 300 ms after each keypress
                    // (`useFilterNavigation.ts`). jQuery-UI's sortable is driven
                    // by `mousedown`/`mouseup` and never sees one, so legacy
                    // emits no `/prp/format/drag/` PATCH while arrowing
                    // (measured on both rails). Without it the press point is
                    // `null`, `moved` reads `true`, and every arrow keypress
                    // would emit a reorder. Real clicks report `detail >= 1`.
                    const press = pressPointRef.current;
                    const moved =
                      press === null ||
                      Math.max(Math.abs(e.clientX - press.x), Math.abs(e.clientY - press.y)) >= 1;
                    pressPointRef.current = null;
                    // `preventClickEvent` — jQuery-UI swallows the click that
                    // trails a real drag OUTRIGHT, so a reorder neither emits
                    // Q22's PATCH nor moves the `.active` row.
                    if (sortable.justDraggedRef.current) return;
                    if (
                      e.detail !== 0 &&
                      moved &&
                      !(e.target as HTMLElement).closest(SORTABLE_CANCEL)
                    ) {
                      onRowPlainClick();
                    }
                    onRowClick(row, domId);
                  }}
                >
                  {/* ★★★ Q13 lives on this element. */}
                  <span
                    className="prօposal_format_field"
                    ref={(el) => {
                      if (el) labelRefs.current.set(row.id, el);
                      else labelRefs.current.delete(row.id);
                    }}
                  >
                    {label}
                  </span>
                  <div className="dropdown  dropdownprop-format col-1">
                    <div className="">
                      {/* ── §6 W9 delete + ★ Q4's double popup ───────────────
                          BOTH the button and the nested icon call
                          `onDeleteClick`, and neither stops the bubble, so one
                          click opens two identical popups — and then bubbles on
                          to the row's `onClick` for `rowClick`, exactly as the
                          three delegated legacy handlers do. */}
                      {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
                      <button
                        data-parent=".left-column-proposal-scrollable"
                        data-id={domId}
                        className={`btn ka-has-tooltip dropdown-toggle icon-more-actions btn-more delete-btn-content delete${
                          topRows.has(row.id) ? ' top' : ''
                        }`}
                        type="button"
                        data-toggle="dropdown"
                        tooltip="Delete"
                        onMouseOver={(e) => handleDeleteMouseOver(row.id, e.target as HTMLElement)}
                        onClick={() => onDeleteClick(domId)}
                      >
                        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
                        <i
                          data-id={domId}
                          className="icon-trash delete"
                          onClick={() => onDeleteClick(domId)}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </Fragment>
            );
          })}
          {placeholderIndex !== null && placeholderIndex >= rows.length && <ProposalRailPlaceholder />}
          {/* `proposal.tmpl:37` — the "append to the end" drop target, absent
              from `proposal.format.tmpl`. */}
          {variant === 'render' && <div className="select-row empty-row" />}
        </div>
      </div>
    </div>
  );
};

/**
 * jQuery-UI's `ui-sortable-placeholder`: the invisible stand-in the sortable
 * leaves in the list so the rows part around the incoming item.
 *
 * ⚠ jQuery-UI's `_createPlaceholder` clones the dragged element's tag and class
 * list, so a library drop really opens an `<a>`-shaped gap the height of the
 * library anchor. That half is not reproduced — a row-shaped gap is what the
 * user is aiming at and the element is invisible either way. The half that IS
 * preserved is the absence of `data-index`, so the placeholder can never disturb
 * the renumbering (here `data-index` is the map position, so it cannot).
 */
const ProposalRailPlaceholder = ({ height }: { height?: number }) => (
  <div
    className="col-xs-12 item-for-drag select-row ui-sortable-placeholder"
    /* An internal drag pins the hole to the lifted row's own measured height,
       so the list's total height cannot change while it travels. A library
       drag leaves it to `.select-row`'s 40px, as before. */
    style={
      height === undefined
        ? { visibility: 'hidden' }
        : { visibility: 'hidden', height, minHeight: height, maxHeight: height }
    }
  />
);

export default ProposalFormatRail;
